/**
 * App Usage Page
 *
 * Two modes on the same tab:
 *   Engaged        — church/personal habit WAU/DAU/MAU + attendance groups
 *   Opened the app — existing all-up rollup (login / session restore)
 */

import { useState, useEffect, useMemo } from 'react';
import { founderDashboardAPI } from '../../services/api';
import { useEnvironment } from '../../context/EnvironmentContext';
import KPICard from '../../components/founder-dashboard/KPICard';
import TrendChart from '../../components/founder-dashboard/TrendChart';
import RefreshIndicator from '../../components/founder-dashboard/RefreshIndicator';
import {
  UserIcon,
  UsersIcon,
  UserGroupIcon,
  ChartBarIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';

const FEATURE_COLORS = {
  prayer: '#3B82F6',
  attendance: '#22C55E',
  event: '#F59E0B',
  chat: '#EF4444',
  group: '#8B5CF6',
  announcement: '#EC4899',
  ai_chat_search: '#14B8A6',
  ai_prayer_search: '#F97316',
  auth: '#6366F1',
};

const HABIT_ROWS = [
  { key: 'attendance', label: 'Attendance (present + recorder)', color: '#22C55E' },
  { key: 'prayer', label: 'Prayer submitted', color: '#3B82F6' },
  { key: 'chat', label: 'Chat sent', color: '#EF4444' },
  { key: 'rsvp', label: 'Event RSVP', color: '#F59E0B' },
];

const APP_USAGE_PERIODS = [
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '180d', label: '180d' },
  { value: 'ytd', label: 'YTD' },
  { value: '365d', label: '365d' },
  { value: '730d', label: '730d' },
];

function titleCase(str) {
  return str
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function toDateStr(value) {
  if (!value) return null;
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function addDays(isoDate, days) {
  const date = toDateStr(isoDate);
  if (!date) return null;
  const [year, month, day] = date.split('-').map(Number);
  const next = new Date(Date.UTC(year, month - 1, day + days));
  return next.toISOString().slice(0, 10);
}

function formatRangeLabel(rows) {
  if (!rows || rows.length === 0) return null;
  const fmt = (iso) => {
    const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return String(iso);
    const d = new Date(+m[1], +m[2] - 1, +m[3]);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };
  return `${fmt(rows[0].date)} - ${fmt(rows[rows.length - 1].date)} (${rows.length} days)`;
}

function changeVs(series, key, daysAgo) {
  if (!series?.length) return undefined;
  const latest = series[series.length - 1];
  const prevDate = addDays(latest.date, -daysAgo);
  const previous = series.find((row) => toDateStr(row.date) === prevDate);
  if (!previous || !previous[key]) return undefined;
  return (latest[key] - previous[key]) / previous[key];
}

export default function AppUsagePage() {
  const { currentEnv } = useEnvironment();
  const [mode, setMode] = useState('engaged');
  const [audience, setAudience] = useState('church');
  const [summary, setSummary] = useState(null);
  const [trends, setTrends] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityTrends, setActivityTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null);
  const [period, setPeriod] = useState('30d');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, trendsRes, activityRes, activityTrendsRes] = await Promise.all([
        founderDashboardAPI.getAppUsageSummary(),
        founderDashboardAPI.getAppUsageTrends(period),
        founderDashboardAPI.getAppUsageActivity(),
        founderDashboardAPI.getAppUsageActivityTrends(period),
      ]);
      setSummary(summaryRes.data);
      setTrends(trendsRes.data);
      setActivity(activityRes.data);
      setActivityTrends(activityTrendsRes.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[AppUsagePage] Error fetching data:', err);
      const rawError = err.response?.data?.error || err.message || 'Failed to load data';
      setError(typeof rawError === 'string' ? rawError : JSON.stringify(rawError));
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSummary(null);
    setTrends(null);
    setActivity(null);
    setActivityTrends(null);
    setError(null);
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentEnv, period]);

  const allUpChartData = useMemo(() => {
    if (!trends?.allUp) return [];
    return trends.allUp.map((d) => ({
      date: d.date,
      DAU: d.dau,
      WAU: d.wau,
      MAU: d.mau,
      Stickiness: d.mau > 0 ? parseFloat(((d.wau / d.mau) * 100).toFixed(1)) : null,
    }));
  }, [trends]);

  const habitSeries = activityTrends?.habit?.[audience] || [];

  const habitChartData = useMemo(() => {
    return habitSeries.map((d) => ({
      date: d.date,
      DAU: d.dau,
      WAU: d.wau,
      MAU: d.mau,
      Stickiness: d.mau > 0 ? parseFloat(((d.wau / d.mau) * 100).toFixed(1)) : null,
    }));
  }, [habitSeries]);

  const featureChartData = useMemo(() => {
    if (!trends?.features) return [];
    const byDate = {};
    trends.features.forEach((row) => {
      if (!byDate[row.date]) byDate[row.date] = { date: row.date };
      byDate[row.date][row.feature] = row.dau;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [trends]);

  const featureNames = useMemo(() => {
    if (!trends?.features) return [];
    const names = new Set(trends.features.map((f) => f.feature));
    return [...names];
  }, [trends]);

  const allUp = summary?.allUp || {};
  const stickiness = allUp.mau > 0 ? allUp.wau / allUp.mau : null;
  const habit = activity?.habit?.[audience] || { dau: 0, wau: 0, mau: 0 };
  const habitStickiness = habit.mau > 0 ? habit.wau / habit.mau : null;
  const groups = activity?.attendanceGroups?.[audience] || { last7d: 0, prev7d: 0 };
  const attendanceChange = groups.prev7d
    ? (groups.last7d - groups.prev7d) / groups.prev7d
    : undefined;

  const dataRangeLabel = formatRangeLabel(
    mode === 'engaged' ? habitChartData : allUpChartData
  );

  const subtitle = mode === 'engaged'
    ? `${audience === 'personal' ? 'Personal groups' : 'Church members'} who used Flock — attendance, prayer, chat, or RSVP`
    : 'Anyone who opened Flock — login and session restore, including never-joined';

  if (error && dataEnv === currentEnv) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-800 font-medium">Error loading app usage data</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
        <button
          onClick={fetchData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">App Usage</h2>
          <p className="text-gray-500 mt-1">{subtitle}</p>
        </div>
        <RefreshIndicator
          lastUpdated={activity?.asOf || summary?.timestamp}
          onRefresh={fetchData}
          loading={loading}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div
          className="inline-flex gap-1 rounded-full border border-gray-300 bg-white p-1"
          role="tablist"
          aria-label="Usage meaning"
        >
          <button
            type="button"
            aria-pressed={mode === 'opened'}
            onClick={() => setMode('opened')}
            className={`min-h-11 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              mode === 'opened'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:text-blue-700'
            }`}
          >
            Opened the app
          </button>
          <button
            type="button"
            aria-pressed={mode === 'engaged'}
            onClick={() => setMode('engaged')}
            className={`min-h-11 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              mode === 'engaged'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:text-blue-700'
            }`}
          >
            Engaged
          </button>
        </div>
        {mode === 'engaged' && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-600">Audience</span>
            <button
              type="button"
              aria-pressed={audience === 'church'}
              onClick={() => setAudience('church')}
              className={`min-h-10 rounded-full border px-3 py-1.5 text-sm font-semibold ${
                audience === 'church'
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-gray-300 bg-white text-gray-700'
              }`}
            >
              Church
            </button>
            <button
              type="button"
              aria-pressed={audience === 'personal'}
              onClick={() => setAudience('personal')}
              className={`min-h-10 rounded-full border px-3 py-1.5 text-sm font-semibold ${
                audience === 'personal'
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-gray-300 bg-white text-gray-700'
              }`}
            >
              Personal
            </button>
          </div>
        )}
      </div>

      {mode === 'engaged' ? (
        <section>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            <KPICard
              title="DAU"
              value={habit.dau}
              change={changeVs(habitSeries, 'dau', 1)}
              format="number"
              icon={UserIcon}
              loading={loading}
            />
            <KPICard
              title="WAU"
              value={habit.wau}
              change={changeVs(habitSeries, 'wau', 7)}
              format="number"
              icon={UsersIcon}
              loading={loading}
            />
            <KPICard
              title="MAU"
              value={habit.mau}
              change={changeVs(habitSeries, 'mau', 28)}
              format="number"
              icon={UserGroupIcon}
              loading={loading}
            />
            <KPICard
              title="Stickiness"
              value={habitStickiness}
              change={changeVs(
                habitSeries.map((row) => ({
                  ...row,
                  stickiness: row.mau > 0 ? row.wau / row.mau : 0,
                })),
                'stickiness',
                7
              )}
              format="percent"
              icon={ChartBarIcon}
              loading={loading}
            />
            <KPICard
              title="Churches w/ Attendance"
              value={groups.last7d}
              change={attendanceChange}
              format="number"
              icon={CalendarDaysIcon}
              loading={loading}
            />
          </div>
        </section>
      ) : (
        <section>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard
              title="DAU"
              value={allUp.dau}
              change={allUp.dauChange ?? undefined}
              format="number"
              icon={UserIcon}
              loading={loading}
            />
            <KPICard
              title="WAU"
              value={allUp.wau}
              change={allUp.wauChange ?? undefined}
              format="number"
              icon={UsersIcon}
              loading={loading}
            />
            <KPICard
              title="MAU"
              value={allUp.mau}
              change={allUp.mauChange ?? undefined}
              format="number"
              icon={UserGroupIcon}
              loading={loading}
            />
            <KPICard
              title="Stickiness"
              value={stickiness}
              change={allUp.stickinessChange ?? undefined}
              format="percent"
              icon={ChartBarIcon}
              loading={loading}
            />
          </div>
        </section>
      )}

      <section
        className="-mt-2 flex flex-wrap items-center justify-start gap-3 sm:justify-end"
        aria-label="Trend controls"
      >
        <span className="text-sm font-medium text-gray-600">Trend range</span>
        <div
          className="flex flex-wrap gap-2"
          role="group"
          aria-label="App usage trend range"
        >
          {APP_USAGE_PERIODS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              aria-pressed={period === value}
              disabled={loading}
              onClick={() => setPeriod(value)}
              className={`min-h-11 cursor-pointer rounded-full border px-4 py-2 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60 ${
                period === value
                  ? 'border-blue-600 bg-blue-600 text-white shadow-sm ring-2 ring-blue-100'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-blue-400 hover:text-blue-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </section>

      {mode === 'engaged' ? (
        <>
          <section>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-gray-800">
                  Used Flock — {audience} habit DAU, WAU & MAU
                </h3>
                {dataRangeLabel && (
                  <p className="text-xs text-gray-400 mt-0.5">
                    {dataRangeLabel} · America/Chicago
                  </p>
                )}
              </div>
              {loading ? (
                <div className="h-64 animate-pulse bg-gray-100 rounded"></div>
              ) : (
                <TrendChart
                  data={habitChartData}
                  lines={[
                    { key: 'DAU', color: '#3B82F6', name: 'Habit DAU', yAxisId: 'left' },
                    { key: 'WAU', color: '#22C55E', name: 'Habit WAU', yAxisId: 'left' },
                    { key: 'MAU', color: '#8B5CF6', name: 'Habit MAU', yAxisId: 'left' },
                    {
                      key: 'Stickiness',
                      color: '#6B7280',
                      name: 'Stickiness',
                      yAxisId: 'right',
                      strokeDasharray: '2 3',
                      format: 'percent',
                    },
                  ]}
                  rightAxisLabel="Stickiness"
                  height={300}
                />
              )}
            </div>
          </section>

          <section>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                What counted as engaged
              </h3>
              {loading ? (
                <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Habit</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-600">DAU</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-600">WAU</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-600">MAU</th>
                      </tr>
                    </thead>
                    <tbody>
                      {HABIT_ROWS.map((habitRow) => {
                        const stats = activity?.habitBreakdown?.[habitRow.key]?.[audience] || {
                          dau: 0,
                          wau: 0,
                          mau: 0,
                        };
                        return (
                          <tr key={habitRow.key} className="border-b border-gray-100">
                            <td className="py-3 px-4 font-medium text-gray-900">
                              <span
                                className="inline-block w-3 h-3 rounded-full mr-2"
                                style={{ backgroundColor: habitRow.color }}
                              />
                              {habitRow.label}
                            </td>
                            <td className="text-right py-3 px-4 text-gray-700">
                              {(stats.dau || 0).toLocaleString()}
                            </td>
                            <td className="text-right py-3 px-4 text-gray-700">
                              {(stats.wau || 0).toLocaleString()}
                            </td>
                            <td className="text-right py-3 px-4 text-gray-700">
                              {(stats.mau || 0).toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-3 leading-relaxed">
                Distinct people, not summed. QR generate, prayer lifts, event edits, AI search,
                and token refresh do not count. The per-feature audit table stays on{' '}
                <strong>Opened the app</strong>. Chat before chat_analytics_daily existed
                undercounts habit.
              </p>
            </div>
          </section>
        </>
      ) : (
        <>
          <section>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-gray-800">
                  Daily, Weekly & Monthly Active Users
                </h3>
                {dataRangeLabel && (
                  <p className="text-xs text-gray-400 mt-0.5">{dataRangeLabel}</p>
                )}
              </div>
              {loading ? (
                <div className="h-64 animate-pulse bg-gray-100 rounded"></div>
              ) : (
                <TrendChart
                  data={allUpChartData}
                  lines={[
                    { key: 'DAU', color: '#3B82F6', name: 'DAU', yAxisId: 'left' },
                    { key: 'WAU', color: '#22C55E', name: 'WAU', yAxisId: 'left' },
                    { key: 'MAU', color: '#8B5CF6', name: 'MAU', yAxisId: 'left' },
                    {
                      key: 'Stickiness',
                      color: '#6B7280',
                      name: 'Stickiness',
                      yAxisId: 'right',
                      strokeDasharray: '2 3',
                      format: 'percent',
                    },
                  ]}
                  rightAxisLabel="Stickiness"
                  height={300}
                />
              )}
            </div>
          </section>

          <section>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-xl font-semibold text-gray-800 mb-4">
                Per-Feature Engagement
              </h3>
              {loading ? (
                <div className="h-48 animate-pulse bg-gray-100 rounded"></div>
              ) : (summary?.features || []).length === 0 ? (
                <p className="text-gray-400 text-center py-8">
                  No feature data yet. The rollup cron runs nightly at 1 AM CST.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-600">Feature</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-600">DAU</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-600">WAU</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-600">MAU</th>
                        <th className="text-right py-3 px-4 font-semibold text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(summary?.features || []).map((f) => (
                        <tr
                          key={f.feature}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="py-3 px-4 font-medium text-gray-900 flex items-center gap-2">
                            <span
                              className="inline-block w-3 h-3 rounded-full"
                              style={{ backgroundColor: FEATURE_COLORS[f.feature] || '#6B7280' }}
                            />
                            {titleCase(f.feature)}
                          </td>
                          <td className="text-right py-3 px-4 text-gray-700">
                            {f.dau.toLocaleString()}
                          </td>
                          <td className="text-right py-3 px-4 text-gray-700">
                            {f.wau.toLocaleString()}
                          </td>
                          <td className="text-right py-3 px-4 text-gray-700">
                            {f.mau.toLocaleString()}
                          </td>
                          <td className="text-right py-3 px-4 text-gray-700">
                            {f.actionCount.toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-3">
                Not church-split. Attendance here is leaders who wrote an audit row, not members
                marked present.
              </p>
            </div>
          </section>

          <section>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="mb-4">
                <h3 className="text-xl font-semibold text-gray-800">
                  Feature Adoption (DAU by Feature)
                </h3>
                {dataRangeLabel && (
                  <p className="text-xs text-gray-400 mt-0.5">{dataRangeLabel}</p>
                )}
              </div>
              {loading ? (
                <div className="h-64 animate-pulse bg-gray-100 rounded"></div>
              ) : featureChartData.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  No feature trend data available yet.
                </div>
              ) : (
                <TrendChart
                  data={featureChartData}
                  lines={featureNames.map((name) => ({
                    key: name,
                    color: FEATURE_COLORS[name] || '#6B7280',
                    name: titleCase(name),
                  }))}
                  height={300}
                />
              )}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
